import { useState, useEffect, useRef } from "react";
import LandingNavbar from "../../components/landing/landing-navbar";
import LandingFooter from "../../components/landing/landing-footer";
import LandingImageCarousel from "../../components/landing/landing-image-carousel";
import { apiService } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Bed, Phone, Clock, Mail, MapPin, Gift } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/auth-context";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useToast } from "../../hooks/use-toast";
import { getTenantId } from "../../lib/config";

interface RoomType {
  id: string;
  name: string;
  description?: string;
  maxCapacity: number;
  basePrice?: number;
  _count?: {
    rooms: number;
  };
}

interface Room {
  id: string;
  roomNumber: string;
  title?: string;
  roomTypeId?: string;
  roomType?: string;
  roomTypeRef?: {
    id: string;
    name: string;
    description?: string;
  };
  companyId: string;
  company?: {
    id: string;
    name: string;
  };
  isActive: boolean;
  basePricePerNight: number;
  currency?: string;
  description?: string;
  capacity: number;
  amenities?: Array<{
    id: string;
    amenity: {
      id: string;
      name: string;
      icon?: string;
    };
  }>;
  images?: Array<{
    id: string;
    imageUrl: string;
    isPrimary: boolean;
    title?: string;
  }>;
  reservations?: Array<{
    id: string;
    status: string;
    checkInDate: string;
    checkOutDate: string;
  }>;
}

interface Company {
  id: string;
  name: string;
}



export default function AccommodationPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [filteredRooms, setFilteredRooms] = useState<Room[]>([]); // Derived filtered rooms
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [bookingForm, setBookingForm] = useState({
    checkInDate: '',
    checkOutDate: '',
    numberOfGuests: '1',
    specialRequests: '',
    promotionId: '',
  });
  const [isCreatingReservation, setIsCreatingReservation] = useState(false);
  const [guestId, setGuestId] = useState<string | null>(null);
  const [promotions, setPromotions] = useState<any[]>([]);
  const [appliedPromotions, setAppliedPromotions] = useState<any[]>([]);
  const [selectedPromotion, setSelectedPromotion] = useState<any>(null);
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(false);
  const [isWelcomeSectionInView, setIsWelcomeSectionInView] = useState(false);
  const [roomsToShow, setRoomsToShow] = useState(8); // Show 8 rooms initially
  const welcomeSectionRef = useRef<HTMLDivElement>(null);

  // Trigger description animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsDescriptionVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Scroll-based animation for welcome section
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsWelcomeSectionInView(entry.isIntersecting);
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    if (welcomeSectionRef.current) {
      observer.observe(welcomeSectionRef.current);
    }

    return () => {
      if (welcomeSectionRef.current) {
        observer.unobserve(welcomeSectionRef.current);
      }
    };
  }, []);

  // Check if user just logged in and has a pending booking
  useEffect(() => {
    if (isAuthenticated && user && rooms.length > 0) {
      const bookingRoomId = localStorage.getItem('bookingRoomId');
      if (bookingRoomId) {
        // Find the room and show booking dialog
        const room = rooms.find(r => r.id === bookingRoomId);
        if (room) {
          setSelectedRoom(room);
          setShowBookingDialog(true);
          findOrCreateGuest(room.companyId);
        }
        // Clear the stored booking room ID
        localStorage.removeItem('bookingRoomId');
        localStorage.removeItem('bookingReturnUrl');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, rooms]);

  useEffect(() => {
    const loadAccommodations = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get tenantId dynamically - uses user's tenantId if logged in, falls back to tenant_demo
        const tenantId = getTenantId();

        // Use public endpoint that doesn't require authentication
        const response = await fetch(`${apiService['baseUrl']}/api/public/accommodations?tenantId=${tenantId}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch accommodations: ${response.statusText}`);
        }

        const data = await response.json();

        if (data.success && data.data) {
          // const activeCompanies = data.data.companies || []; // Removed unused variable
          const allRooms = data.data.rooms || [];

          // Extract unique companies from rooms ONLY (to match My Account behavior)
          // This ensures we only show tabs for companies that actually have rooms displayed
          const uniqueCompanies = new Map();

          allRooms.forEach((room: Room) => {
            if (room.company && room.company.id && room.company.name) {
              uniqueCompanies.set(room.company.id, room.company);
            }
          });

          setCompanies(Array.from(uniqueCompanies.values()));
          setRooms(allRooms);
          setFilteredRooms(allRooms); // Initialize filtered rooms with all rooms

          // Extract unique room types from rooms
          const roomTypesMap = new Map<string, RoomType>();
          allRooms.forEach((room: Room) => {
            if (room.roomTypeRef) {
              roomTypesMap.set(room.roomTypeRef.id, {
                id: room.roomTypeRef.id,
                name: room.roomTypeRef.name,
                description: room.roomTypeRef.description,
                maxCapacity: room.capacity,
                basePrice: room.basePricePerNight,
              });
            }
          });
          setRoomTypes(Array.from(roomTypesMap.values()));
        } else {
          setRooms([]);
          setRoomTypes([]);
          setCompanies([]);
        }
      } catch (err: any) {
        console.error("Failed to load accommodations:", err);
        setError(err.message || "Failed to load accommodations");
        setRooms([]);
        setRoomTypes([]);
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };

    loadAccommodations();
  }, []);

  // Filter rooms when selectedCompanyId changes
  useEffect(() => {
    if (selectedCompanyId === 'all') {
      setFilteredRooms(rooms.filter(room => room.isActive));
    } else {
      setFilteredRooms(rooms.filter(room => room.companyId === selectedCompanyId && room.isActive));
    }
  }, [selectedCompanyId, rooms]);

  // Reset roomsToShow when filters change or rooms are loaded
  useEffect(() => {
    setRoomsToShow(8);
  }, [selectedCompanyId, filteredRooms.length]);

  // Determine if a room type is active (has at least one active room)
  const isRoomTypeActive = (roomTypeId: string): boolean => {
    return rooms.some(
      (room) => room.roomTypeId === roomTypeId && room.isActive
    );
  };

  // Get active room count for a room type
  const getActiveRoomCount = (roomTypeId: string): number => {
    return rooms.filter(
      (room) => room.roomTypeId === roomTypeId && room.isActive
    ).length;
  };

  // Get representative image for a room
  const getRoomImage = (room: Room): string => {
    if (room.images && room.images.length > 0) {
      const primaryImage = room.images.find((img) => img.isPrimary);
      const imageUrl = primaryImage?.imageUrl || room.images[0]?.imageUrl;

      if (imageUrl) {
        // Handle both relative and absolute URLs
        if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
          return imageUrl;
        }
        // Construct full URL for relative paths
        const baseUrl = apiService['baseUrl'] || '';
        return imageUrl.startsWith("/") ? `${baseUrl}${imageUrl}` : `${baseUrl}/${imageUrl}`;
      }
    }

    // Fallback to default image
    return "/images/webflow/IWACU-002.jpg";
  };

  // Get room amenities as string
  const getRoomAmenities = (room: Room): string => {
    if (!room.amenities || room.amenities.length === 0) return '';
    return room.amenities.map(a => a.amenity?.name || '').filter(Boolean).join(', ');
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = 'RWF') => {
    if (!amount) return 'N/A';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return `0 ${currency}`;
    
    if (currency === 'RWF' || currency === 'FRW') {
      return `${Math.round(numAmount).toLocaleString('en-US')} FRW`;
    } else if (currency === 'USD') {
      return `$${numAmount.toFixed(2)}`;
    } else {
      return `${currency} ${numAmount.toFixed(2)}`;
    }
  };

  // Check if a room is reserved
  const isRoomReserved = (room: Room): boolean => {
    if (!room.reservations || room.reservations.length === 0) return false;
    return room.reservations.some((r: any) =>
      r.status !== 'CANCELLED' && r.status !== 'CHECKED_OUT'
    );
  };

  // Get booked dates for a room (formatted string)
  const getRoomBookedDates = (room: Room): string => {
    if (!room.reservations || room.reservations.length === 0) return '';

    const activeReservations = (room.reservations as any[])
      .filter((r: any) => r.status !== 'CANCELLED' && r.status !== 'CHECKED_OUT')
      .sort((a: any, b: any) =>
        new Date(a.checkInDate).getTime() - new Date(b.checkInDate).getTime()
      );

    if (activeReservations.length === 0) return '';

    // Format dates: "Jan 15 - Jan 20, Jan 25 - Jan 30"
    const dateRanges = activeReservations.map((res: any) => {
      const checkIn = new Date(res.checkInDate);
      const checkOut = new Date(res.checkOutDate);
      const formatDate = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${formatDate(checkIn)} - ${formatDate(checkOut)}`;
    });

    return dateRanges.join(', ');
  };

  // Handle Book Now click
  const handleBookNow = (room: Room) => {
    if (!isAuthenticated) {
      // Store the room ID and redirect to login
      localStorage.setItem('bookingRoomId', room.id);
      localStorage.setItem('bookingReturnUrl', '/accommodation');
      navigate('/login');
      return;
    }

    // User is logged in, show booking dialog
    setSelectedRoom(room);
    setBookingForm({
      checkInDate: '',
      checkOutDate: '',
      numberOfGuests: '1',
      specialRequests: '',
      promotionId: '',
    });
    setSelectedPromotion(null);
    setAppliedPromotions([]);
    setShowBookingDialog(true);

    // Try to find or create guest for this user
    findOrCreateGuest(room.companyId);

    // Load promotions for this company
    loadPromotions(room.companyId);
  };

  // Load promotions for a company
  const loadPromotions = async (companyId: string) => {
    try {
      const promos = await apiService.getPromotions({
        companyId,
        isActive: true,
      });
      setPromotions(Array.isArray(promos) ? promos : []);
    } catch (error: any) {
      console.error('Failed to load promotions:', error);
      setPromotions([]);
    }
  };

  // Auto-apply valid promotions
  const autoApplyPromotion = (checkInDate: string, checkOutDate: string) => {
    if (!selectedRoom || !checkInDate || !checkOutDate) return;

    const promosToCheck = promotions.filter((p: any) =>
      p.companyId === selectedRoom.companyId
    );

    if (promosToCheck.length === 0) {
      setSelectedPromotion(null);
      setAppliedPromotions([]);
      setBookingForm(prev => ({ ...prev, promotionId: '' }))
      return;
    }

    const now = new Date()
    const checkIn = new Date(checkInDate)
    const nights = Math.ceil(
      (new Date(checkOutDate).getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)
    )

    // Filter valid promotions
    const validPromotions = promosToCheck.filter((promo: any) => {
      const validFrom = new Date(promo.validFrom)
      const validTo = new Date(promo.validTo)

      // Check if promotion is active and valid
      if (!promo.isActive) return false
      if (validFrom > now || validTo < now) return false
      if (checkIn < validFrom || checkIn > validTo) return false

      // Check min/max stay
      if (promo.minStay && nights < promo.minStay) return false
      if (promo.maxStay && nights > promo.maxStay) return false

      // Check max uses
      if (promo.maxUses && promo.currentUses >= promo.maxUses) return false

      // Check applicable room types
      if (promo.applicableRoomTypes && Array.isArray(promo.applicableRoomTypes) && promo.applicableRoomTypes.length > 0) {
        if (!promo.applicableRoomTypes.includes(selectedRoom.roomTypeId)) return false
      }

      return true
    })

    if (validPromotions.length === 0) {
      setSelectedPromotion(null)
      setAppliedPromotions([])
      setBookingForm(prev => ({ ...prev, promotionId: '' }))
      return
    }

    // Apply ALL valid promotions and calculate combined discount
    const baseAmount = (selectedRoom.basePricePerNight || 0) * nights
    let totalDiscount = 0
    let remainingAmount = baseAmount

    // Calculate discount for each promotion and apply them sequentially
    const promotionsWithDiscounts = validPromotions.map((promo: any) => {
      let discount = 0
      if (promo.discountType === 'PERCENTAGE') {
        // Apply percentage to remaining amount
        discount = remainingAmount * (parseFloat(promo.discountValue.toString()) / 100)
        remainingAmount = remainingAmount - discount
      } else if (promo.discountType === 'FIXED_AMOUNT') {
        discount = parseFloat(promo.discountValue.toString())
        remainingAmount = Math.max(0, remainingAmount - discount)
      } else if (promo.discountType === 'FREE_NIGHT') {
        const freeNights = parseInt(promo.discountValue.toString())
        discount = (selectedRoom.basePricePerNight || 0) * freeNights
        remainingAmount = Math.max(0, remainingAmount - discount)
      }

      totalDiscount += discount
      return { ...promo, calculatedDiscount: discount }
    })

    // Apply all promotions
    if (promotionsWithDiscounts.length > 0) {
      // Store the first promotion ID (schema only supports one, but we apply all discounts)
      setSelectedPromotion(promotionsWithDiscounts[0]) // Keep first for display
      setAppliedPromotions(promotionsWithDiscounts) // Store all applied promotions
      setBookingForm(prev => ({ ...prev, promotionId: promotionsWithDiscounts[0].id }))
    } else {
      setSelectedPromotion(null)
      setAppliedPromotions([])
      setBookingForm(prev => ({ ...prev, promotionId: '' }))
    }
  }

  // Auto-apply promotion when dates change or promotions are loaded
  useEffect(() => {
    if (selectedRoom && bookingForm.checkInDate && bookingForm.checkOutDate && promotions.length > 0) {
      autoApplyPromotion(bookingForm.checkInDate, bookingForm.checkOutDate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingForm.checkInDate, bookingForm.checkOutDate, promotions.length, selectedRoom?.id])

  // Find or create guest for logged-in user
  const findOrCreateGuest = async (companyId: string) => {
    if (!user) return;

    try {
      // Validate email format before using it
      const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
      };

      const userEmail = user.email && isValidEmail(user.email) ? user.email : null;

      // Try to find existing guest by searching with email (only if valid)
      if (userEmail) {
        const guests = await apiService.getHotelGuests({ companyId, search: userEmail });
        const existingGuest = Array.isArray(guests)
          ? guests.find((g: any) => g.email === userEmail)
          : null;

        if (existingGuest) {
          setGuestId(existingGuest.id);
          return;
        }
      }

      // Create new guest from user data
      // Ensure firstName and lastName are valid (required fields)
      const firstName = (user.firstName || userEmail?.split('@')[0] || 'Guest').trim();
      const lastName = (user.lastName || '').trim() || 'User'; // Ensure lastName is not empty

      if (!firstName || firstName.length === 0) {
        throw new Error('First name is required to create guest profile');
      }

      // Only send required fields and valid optional fields (not empty strings)
      const guestData: any = {
        companyId,
        firstName,
        lastName,
      };

      // Add email only if it's valid
      if (userEmail) {
        guestData.email = userEmail;
      }

      // Include phone number if available
      if (user.phone && user.phone.trim()) {
        guestData.phone = user.phone.trim();
      }

      // Include other user details if available
      if (user.country && user.country.trim()) {
        guestData.country = user.country.trim();
      }

      if (user.passportNumber && user.passportNumber.trim()) {
        guestData.passportNumber = user.passportNumber.trim();
      }

      if (user.idNumber && user.idNumber.trim()) {
        guestData.idNumber = user.idNumber.trim();
      }

      const newGuest = await apiService.createHotelGuest(guestData);

      setGuestId(newGuest.id);
    } catch (error: any) {
      console.error('Failed to find/create guest:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to set up guest profile. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Handle booking submission
  const handleBookingSubmit = async () => {
    if (!selectedRoom || !guestId) {
      toast({
        title: 'Error',
        description: 'Please wait while we set up your booking...',
        variant: 'destructive',
      });
      return;
    }

    if (!bookingForm.checkInDate || !bookingForm.checkOutDate) {
      toast({
        title: 'Validation Error',
        description: 'Please select check-in and check-out dates',
        variant: 'destructive',
      });
      return;
    }

    setIsCreatingReservation(true);
    try {
      const checkInDate = new Date(bookingForm.checkInDate).toISOString();
      const checkOutDate = new Date(bookingForm.checkOutDate).toISOString();
      const numberOfGuests = parseInt(bookingForm.numberOfGuests) || 1;

      // First, check room availability for the selected dates
      const availabilityResult = await apiService.checkRoomAvailability({
        companyId: selectedRoom.companyId,
        checkInDate: bookingForm.checkInDate,
        checkOutDate: bookingForm.checkOutDate,
        roomTypeId: selectedRoom.roomTypeId,
        numberOfGuests,
      });

      // Check if the selected room is still available
      const isRoomAvailable = availabilityResult.rooms.some((room: any) => room.id === selectedRoom.id);

      if (!isRoomAvailable || availabilityResult.rooms.length === 0) {
        toast({
          title: 'Room No Longer Available',
          description: `The selected room is no longer available for the dates ${bookingForm.checkInDate} to ${bookingForm.checkOutDate}. Please select different dates or another room.`,
          variant: 'destructive',
        });
        setIsCreatingReservation(false);
        return;
      }

      // Calculate total amount with promotions
      const nights = Math.ceil((new Date(checkOutDate).getTime() - new Date(checkInDate).getTime()) / (1000 * 60 * 60 * 24));
      let baseAmount = selectedRoom.basePricePerNight * nights;
      let totalAmount = baseAmount;
      let discountAmount = 0;

      // Apply all promotion discounts
      if (appliedPromotions.length > 0) {
        let remainingAmount = baseAmount;
        appliedPromotions.forEach((promo: any) => {
          let promoDiscount = 0;
          if (promo.discountType === 'PERCENTAGE') {
            // Apply percentage to remaining amount
            promoDiscount = remainingAmount * (parseFloat(promo.discountValue.toString()) / 100);
            remainingAmount = remainingAmount - promoDiscount;
          } else if (promo.discountType === 'FIXED_AMOUNT') {
            promoDiscount = parseFloat(promo.discountValue.toString());
            remainingAmount = Math.max(0, remainingAmount - promoDiscount);
          } else if (promo.discountType === 'FREE_NIGHT') {
            const freeNights = parseInt(promo.discountValue.toString());
            promoDiscount = (selectedRoom.basePricePerNight || 0) * freeNights;
            remainingAmount = Math.max(0, remainingAmount - promoDiscount);
          }
          discountAmount += promoDiscount;
        });
        totalAmount = remainingAmount;
      }

      await apiService.createHotelReservation({
        companyId: selectedRoom.companyId,
        roomId: selectedRoom.id,
        guestId,
        checkInDate,
        checkOutDate,
        numberOfGuests,
        totalAmount,
        depositAmount: 0,
        specialRequests: bookingForm.specialRequests || undefined,
        promotionId: bookingForm.promotionId || undefined,
        appliedPromotionIds: appliedPromotions.map((p: any) => p.id),
      });

      toast({
        title: 'Success',
        description: 'Reservation created successfully!',
      });

      setShowBookingDialog(false);
      setSelectedRoom(null);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create reservation',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingReservation(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <LandingNavbar />
      <main className="flex flex-col">
        {/* Hero Section - Dynamic Carousel */}
        <div className="relative w-full">
          <LandingImageCarousel />
          
          {/* Call Now Button - Top Right Overlay */}
          {/* <div className="absolute top-2 right-2 sm:top-4 sm:right-4 z-30">
            <button className="bg-[#d4a574] hover:bg-[#c49564] text-white px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold rounded shadow-lg">
              <a href="tel:+250791874212" className="flex items-center justify-center whitespace-nowrap">
                <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                <span>Call now + 250 791 874 212</span>
              </a>
            </button>
          </div> */}

          {/* Bottom Section - Text Overlay */}
          <div className="absolute bottom-0 left-0 right-0 z-30 flex flex-col items-center justify-end pb-24 sm:pb-28 md:pb-32 pointer-events-none">
            {/* Text with better visibility */}
            {/* <div className="text-center px-4 mb-3 sm:mb-4 md:mb-6">
              <h1
                className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white drop-shadow-2xl"
                style={{
                  textShadow:
                    "0 2px 8px rgba(0, 0, 0, 0.8), 0 0 16px rgba(0, 0, 0, 0.5)",
                }}
              >
                Elegant, quietly, comfortable
              </h1>
            </div> */}

            {/* CHECK AVAILABILITY Button - Commented out */}
            {/* <Link 
              to="/contact" 
              className="bg-[#41464b] hover:bg-[#353a3f] text-white px-4 py-2 sm:px-6 sm:py-2.5 md:px-8 md:py-3 text-xs sm:text-sm md:text-base font-semibold uppercase border-2 border-white rounded inline-block shadow-2xl pointer-events-auto transition-transform hover:scale-105"
            >
              CHECK AVAILABILITY
            </Link> */}
          </div>
        </div>

        {/* Main Content Section */}
        <div
          ref={welcomeSectionRef}
          className="w-full relative py-8 sm:py-12 md:py-16"
          style={{ backgroundColor: "#000000" }}
        >
          <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
              {/* Left Side - Image with 3D zoom animation */}
              <div 
                className={`order-2 md:order-1 transition-all duration-1000 ease-out ${
                  isWelcomeSectionInView 
                    ? 'opacity-100' 
                    : 'opacity-0'
                }`}
                style={{
                  transform: isWelcomeSectionInView 
                    ? 'perspective(1000px) translateZ(0) scale(1)' 
                    : 'perspective(1000px) translateZ(-300px) scale(0.6)'
                }}
              >
                <img
                  src="/images/webflow/main.jpg"
                  alt="Ikoro House Interior"
                  className="w-full h-auto min-h-[250px] sm:min-h-[350px] md:min-h-[400px] object-cover rounded-lg shadow-lg"
                />
              </div>

              {/* Right Side - Text Content with 3D zoom animation */}
              <div 
                className={`order-1 md:order-2 space-y-4 sm:space-y-5 md:space-y-6 transition-all duration-1000 delay-200 ease-out ${
                  isWelcomeSectionInView 
                    ? 'opacity-100' 
                    : 'opacity-0'
                }`}
                style={{
                  transform: isWelcomeSectionInView 
                    ? 'perspective(1000px) translateZ(0) scale(1)' 
                    : 'perspective(1000px) translateZ(-300px) scale(0.6)'
                }}
              >
                <h2
                  className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white"
                >
                  Welcome to our quietly comfortable rooms at Ikoro House.
                </h2>
                <div
                  className="space-y-3 sm:space-y-4 text-sm sm:text-base md:text-lg leading-relaxed text-white"
                >
                  <p 
                    className={`transition-all duration-1000 ease-out ${
                      isDescriptionVisible 
                        ? 'opacity-100 translate-y-0' 
                        : 'opacity-0 -translate-y-10'
                    }`}
                  >
                    ‍Discover the perfect blend of classic style and modern
                    comfort at Ikoro House. Our rooms offer spacious, cozy beds
                    for a peaceful night's rest. For business travelers, each
                    room includes a dedicated workspace and complimentary
                    high-speed WiFi. At Ikoro House, your comfort is paramount.
                    Enjoy 24-hour room service for your cravings. Choose from
                    our six unique room options: Iwacu, Karisimbi, Bisoke,
                    Muhabura, Sabyinyo, and Gahinga. Each room offers a
                    personalized experience. Our team is dedicated to seamlessly
                    blending tranquility and comfort. Whether you're in Kigali
                    for a safari stopover or a business trip, our thoughtfully
                    designed rooms cater to your needs. Experience the best of
                    Kigali in ultimate comfort at Ikoro House.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className="w-full relative py-8 sm:py-12 bg-white"
        >
          {/* Dark Grey Header Bar - Full width */}

          {/* Main Image Container - Max width 7xl, centered */}
          <div className="flex justify-center">
            <div className="w-full max-w-8xl relative mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-4 sm:gap-6">
              {/* Company Filter Tabs */}
              {companies.length > 0 && (
                <div className="mb-8 overflow-x-auto pb-2 scrollbar-hide">
                  <div className="flex justify-center gap-2 min-w-max px-4">
                    <button
                      onClick={() => setSelectedCompanyId('all')}
                      className={`px-5 py-2.5 rounded-full text-sm sm:text-base font-semibold transition-all whitespace-nowrap ${selectedCompanyId === 'all'
                        ? 'shadow-md transform scale-105'
                        : 'hover:bg-gray-100'
                        }`}
                      style={{
                        backgroundColor: selectedCompanyId === 'all' ? '#c39d76' : '#f3f4f6',
                        color: selectedCompanyId === 'all' ? 'white' : '#2C2416',
                        border: selectedCompanyId === 'all' ? 'none' : '1px solid #e5e7eb'
                      }}
                    >
                      All Departments
                    </button>
                    {companies.map((company) => (
                      <button
                        key={company.id}
                        onClick={() => setSelectedCompanyId(company.id)}
                        className={`px-5 py-2.5 rounded-full text-sm sm:text-base font-semibold transition-all whitespace-nowrap ${selectedCompanyId === company.id
                          ? 'shadow-md transform scale-105'
                          : 'hover:bg-gray-100'
                          }`}
                        style={{
                          backgroundColor: selectedCompanyId === company.id ? '#c39d76' : '#f3f4f6',
                          color: selectedCompanyId === company.id ? 'white' : '#2C2416',
                          border: selectedCompanyId === company.id ? 'none' : '1px solid #e5e7eb'
                        }}
                      >
                        {company.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {loading ? (
                <div className="text-center py-12">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#c39d76] mb-4"></div>
                    <p className="text-gray-600 text-lg">Loading rooms...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <p className="text-red-600">{error}</p>
                </div>
              ) : filteredRooms.length === 0 ? (
                <div className="text-center py-12 sm:py-16 md:py-20">
                  <Bed className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto mb-4 sm:mb-6 text-gray-400 opacity-70" />
                  <p className="text-gray-600 text-base sm:text-lg md:text-xl font-medium">
                    No available rooms found
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                    {filteredRooms.slice(0, roomsToShow).map((room) => {
                      const mainImage = getRoomImage(room);

                      return (
                        <div
                        key={room.id}
                        onClick={() => navigate(`/room/${room.id}`)}
                        className="cursor-pointer group"
                      >
                        {/* Room Card - Image with Info Overlay at Bottom */}
                        <div className="relative h-[340px] sm:h-[380px] rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                          {/* Room Image */}
                          <img
                            src={mainImage}
                            alt={room.title || room.roomNumber}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/images/webflow/IWACU-002.jpg';
                            }}
                          />

                          {/* Reserved Badge */}
                          {isRoomReserved(room) && (
                            <div className="absolute top-3 right-3 z-10">
                              <span className="bg-red-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg">
                                Reserved
                              </span>
                            </div>
                          )}

                          {/* Gradient Overlay at Bottom */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                          {/* Room Info - Minimal: just name and price */}
                          <div className="absolute bottom-0 left-0 right-0 p-4 space-y-2">
                            <h3 className="font-semibold text-lg text-white truncate">
                              {room.roomNumber}
                            </h3>

                            {/* Price */}
                            <div className="pt-1">
                              <span className="font-semibold text-xl text-white">
                                {formatCurrency(room.basePricePerNight, room.currency || 'RWF')}
                              </span>
                              <span className="text-white/90 text-sm"> / night</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Load More Button */}
                {filteredRooms.length > roomsToShow && (
                  <div className="flex justify-center mt-8">
                    <Button
                      onClick={() => setRoomsToShow(prev => prev + 8)}
                      className="bg-[#c39d76] hover:bg-[#b38a66] text-white px-8 py-3 text-base font-semibold transition-colors"
                    >
                      Load More Rooms
                    </Button>
                  </div>
                )}
              </>
              )}
            </div>
          </div>
        </div>
      </main>
      <LandingFooter />

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-4 sm:mb-6 md:mb-8" style={{ color: '#2C2416' }}>
              IkoroHouse Booking form
            </DialogTitle>
            {selectedRoom && (
              <DialogDescription className="text-center mb-4 sm:mb-6 text-sm sm:text-base">
                {selectedRoom.title || selectedRoom.roomNumber}
                {selectedRoom.company && ` at ${selectedRoom.company.name}`}
              </DialogDescription>
            )}
          </DialogHeader>
          {selectedRoom && (
            <form onSubmit={(e) => { e.preventDefault(); handleBookingSubmit(); }} className="space-y-4 sm:space-y-6">
              {/* ARRIVING DATE */}
              <div className="flex flex-col md:flex-row md:items-center gap-3 sm:gap-4 md:gap-12">
                <label className="w-full md:w-48 font-semibold text-sm sm:text-base" style={{ color: '#2C2416' }}>
                  ARRIVING DATE:
                </label>
                <Input
                  type="date"
                  placeholder="dd/mm/yyyy"
                  value={bookingForm.checkInDate}
                  onChange={(e) => {
                    setBookingForm({ ...bookingForm, checkInDate: e.target.value })
                    // Auto-apply promotion when dates change
                    if (e.target.value && bookingForm.checkOutDate) {
                      autoApplyPromotion(e.target.value, bookingForm.checkOutDate)
                    }
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  className="flex-1 bg-white border border-gray-300 rounded-none h-10 sm:h-11 text-sm sm:text-base"
                />
              </div>

              {/* DEPARTURE DATE */}
              <div className="flex flex-col md:flex-row md:items-center gap-3 sm:gap-4 md:gap-12">
                <label className="w-full md:w-48 font-semibold text-sm sm:text-base" style={{ color: '#2C2416' }}>
                  DEPARTURE DATE:
                </label>
                <Input
                  type="date"
                  placeholder="dd/mm/yyyy"
                  value={bookingForm.checkOutDate}
                  onChange={(e) => {
                    setBookingForm({ ...bookingForm, checkOutDate: e.target.value })
                    // Auto-apply promotion when dates change
                    if (bookingForm.checkInDate && e.target.value) {
                      autoApplyPromotion(bookingForm.checkInDate, e.target.value)
                    }
                  }}
                  min={bookingForm.checkInDate || new Date().toISOString().split('T')[0]}
                  required
                  className="flex-1 bg-white border border-gray-300 rounded-none h-10 sm:h-11 text-sm sm:text-base"
                />
              </div>

              {/* NUMBER OF GUESTS */}
              <div className="flex flex-col md:flex-row md:items-center gap-3 sm:gap-4 md:gap-12">
                <label className="w-full md:w-48 font-semibold text-sm sm:text-base" style={{ color: '#2C2416' }}>
                  NUMBER OF GUESTS:
                </label>
                <div className="flex-1 flex flex-col sm:flex-row gap-2">
                  <Input
                    type="number"
                    min="1"
                    max={selectedRoom.capacity}
                    value={bookingForm.numberOfGuests}
                    onChange={(e) => setBookingForm({ ...bookingForm, numberOfGuests: e.target.value })}
                    required
                    className="bg-white border border-gray-300 rounded-none h-10 sm:h-11 text-sm sm:text-base"
                  />
                  <p className="text-xs text-gray-500 mt-1 sm:mt-0 sm:ml-2 self-start sm:self-center">
                    Maximum capacity: {selectedRoom.capacity} {selectedRoom.capacity === 1 ? 'guest' : 'guests'}
                  </p>
                </div>
              </div>

              {/* AUTOMATIC PROMOTIONS DISPLAY */}
              {bookingForm.checkInDate && bookingForm.checkOutDate && appliedPromotions.length > 0 && (
                <div className="flex flex-col md:flex-row md:items-start gap-3 sm:gap-4 md:gap-12">
                  <label className="w-full md:w-48 font-semibold pt-2 text-sm sm:text-base" style={{ color: '#2C2416' }}>
                    APPLIED PROMOTIONS:
                  </label>
                  <div className="flex-1 space-y-2">
                    {appliedPromotions.map((promo: any, index: number) => (
                      <div key={promo.id} className="bg-green-50 border border-green-200 p-4 rounded">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-green-100 rounded-full">
                            <Gift className="h-5 w-5 text-green-700" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-green-800">
                                ✓ {promo.name} - Automatically Applied
                              </p>
                              {appliedPromotions.length > 1 && (
                                <Badge variant="secondary" className="bg-green-200 text-green-800 text-xs">
                                  {index + 1} of {appliedPromotions.length}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-green-700 mt-1">
                              {promo.description || 'This promotion has been automatically applied to your booking'}
                            </p>
                            <div className="mt-2 text-xs text-green-600 font-semibold">
                              {promo.discountType === 'PERCENTAGE'
                                ? `${promo.discountValue}% discount`
                                : promo.discountType === 'FIXED_AMOUNT'
                                  ? `${promo.discountValue} ${selectedRoom.currency || 'RWF'} discount`
                                  : `${promo.discountValue} free night(s)`
                              }
                              {promo.calculatedDiscount && (
                                <span className="ml-2">(-{promo.calculatedDiscount.toLocaleString()} {selectedRoom.currency || 'RWF'})</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {appliedPromotions.length > 1 && (
                      <div className="bg-blue-50 border border-blue-200 p-3 rounded mt-2">
                        <p className="text-xs font-semibold text-blue-800">
                          💡 All {appliedPromotions.length} promotions have been automatically applied and combined for maximum savings!
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* SPECIAL REQUESTS */}
              {bookingForm.specialRequests !== undefined && (
                <div className="flex flex-col md:flex-row md:items-start gap-3 sm:gap-4 md:gap-12">
                  <label className="w-full md:w-48 font-semibold pt-2 text-sm sm:text-base" style={{ color: '#2C2416' }}>
                    SPECIAL REQUESTS:
                  </label>
                  <textarea
                    value={bookingForm.specialRequests}
                    onChange={(e) => setBookingForm({ ...bookingForm, specialRequests: e.target.value })}
                    className="flex-1 bg-white border border-gray-300 rounded-none p-2 min-h-[80px] text-sm sm:text-base"
                    placeholder="Any special requests or notes..."
                  />
                </div>
              )}

              {/* PRICE BREAKDOWN */}
              {bookingForm.checkInDate && bookingForm.checkOutDate && (
                <div className="flex flex-col md:flex-row md:items-start gap-3 sm:gap-4 md:gap-12">
                  <div className="w-full md:w-48 font-semibold text-sm sm:text-base" style={{ color: '#2C2416' }}>
                    PRICE BREAKDOWN:
                  </div>
                  <div className="flex-1 bg-gray-50 p-4 space-y-2">
                    {(() => {
                      const nights = Math.ceil(
                        (new Date(bookingForm.checkOutDate).getTime() - new Date(bookingForm.checkInDate).getTime()) / (1000 * 60 * 60 * 24)
                      )
                      const baseAmount = (selectedRoom.basePricePerNight || 0) * nights
                      let discountAmount = 0
                      let totalAmount = baseAmount

                      // Calculate discount from all applied promotions
                      const promotionDiscounts: { promo: any; discount: number }[] = []
                      if (appliedPromotions.length > 0) {
                        let remainingAmount = baseAmount
                        appliedPromotions.forEach((promo: any) => {
                          let promoDiscount = 0
                          if (promo.discountType === 'PERCENTAGE') {
                            // Apply percentage to remaining amount
                            promoDiscount = remainingAmount * (parseFloat(promo.discountValue.toString()) / 100)
                            remainingAmount = remainingAmount - promoDiscount
                          } else if (promo.discountType === 'FIXED_AMOUNT') {
                            promoDiscount = parseFloat(promo.discountValue.toString())
                            remainingAmount = Math.max(0, remainingAmount - promoDiscount)
                          } else if (promo.discountType === 'FREE_NIGHT') {
                            const freeNights = parseInt(promo.discountValue.toString())
                            promoDiscount = (selectedRoom.basePricePerNight || 0) * freeNights
                            remainingAmount = Math.max(0, remainingAmount - promoDiscount)
                          }
                          discountAmount += promoDiscount
                          // Store the calculated discount for this promotion
                          promotionDiscounts.push({ promo, discount: promoDiscount })
                        })
                        totalAmount = remainingAmount
                      }

                      return (
                        <>
                          <div className="flex justify-between text-sm">
                            <span>Base Price ({nights} {nights === 1 ? 'night' : 'nights'} × {selectedRoom.basePricePerNight?.toLocaleString()} {selectedRoom.currency || 'RWF'}):</span>
                            <span className="font-semibold">{baseAmount.toLocaleString()} {selectedRoom.currency || 'RWF'}</span>
                          </div>
                          {promotionDiscounts.length > 0 && (
                            <div className="space-y-1">
                              {promotionDiscounts.map((item, index: number) => {
                                const { promo, discount } = item
                                return (
                                  <div key={promo.id} className="flex justify-between text-sm text-green-600">
                                    <span>Promotion {index + 1}: {promo.name}</span>
                                    <span className="font-semibold">
                                      {discount > 0 ? `-${discount.toLocaleString()}` : discount.toLocaleString()} {selectedRoom.currency || 'RWF'}
                                    </span>
                                  </div>
                                )
                              })}
                              {promotionDiscounts.length > 1 && (
                                <div className="flex justify-between text-sm font-bold text-green-700 pt-1 border-t border-green-300">
                                  <span>Total Promotions Discount:</span>
                                  <span>-{discountAmount.toLocaleString()} {selectedRoom.currency || 'RWF'}</span>
                                </div>
                              )}
                            </div>
                          )}
                          <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-300">
                            <span>Total Amount:</span>
                            <span style={{ color: '#c39d76' }}>{totalAmount.toLocaleString()} {selectedRoom.currency || 'RWF'}</span>
                          </div>
                        </>
                      )
                    })()}
                  </div>
                </div>
              )}

              {/* SUBMIT BUTTON */}
              <div className="flex flex-col md:flex-row md:items-center gap-4 sm:gap-6 pt-4 sm:pt-6">
                <div className="w-full md:w-48 hidden md:block"></div>
                <div className="flex-1 flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowBookingDialog(false)}
                    className="w-full sm:flex-1 bg-white text-black font-semibold uppercase hover:opacity-90 transition-opacity py-2.5 sm:py-3 rounded-none text-sm sm:text-base"
                  >
                    CANCEL
                  </Button>
                  <Button
                    type="submit"
                    disabled={isCreatingReservation || !guestId}
                    className="w-full sm:flex-1 bg-black text-white font-semibold uppercase hover:opacity-90 transition-opacity py-2.5 sm:py-3 rounded-none text-sm sm:text-base"
                  >
                    {isCreatingReservation ? 'BOOKING...' : 'SUBMIT'}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
